import classNames from 'classnames'
import { Fragment, ReactNode, useCallback, useEffect, useMemo } from 'react'

import { get, set } from 'core-components/classes/services/StorageHelper'
import { ICustomHeaderOptions, IHierarchyColumn } from 'core-components/library/list/types'

import { calcHeaderColumnsToRender } from './helpers/calcColumns'
import { ITHeadProps } from './models/ITHead'
import { Th } from './thead/Th'

import styles from './THead.module.scss'

export const THead = <P, R>(props: ITHeadProps<P, R>) => {
  const {
    source,
    columns,
    OnPriorityChange,
    cacheKey,
    hasHierarchy,
    rowActions,
    titleRender,
    isHeaderSticky,
    classes,
    headerIterator,
    headerRowTpl,
    keyField,
    useRowCheckbox,
    enableNav,
    headerCellTpl,
  } = props

  const columnsToRender = useMemo(() => {
    return calcHeaderColumnsToRender(columns, keyField || 'EntryId')
  }, [columns, keyField])

  const renderLine = useCallback(
    (renderedColumn: IHierarchyColumn<P>[], headerLine: number) => {
      if (useRowCheckbox) {
        renderedColumn.unshift({
          title: '',
          field: '',
          hideSettings: true,
        })
      }
      const arrLastItem = renderedColumn.length - 1

      const renderColumns = () => {
        const drawHeaderColumn = (opt: { index: number; headerOptions?: ICustomHeaderOptions }) => {
          const headerCellClasses = classNames(
            opt.headerOptions?.className,
            styles.headCell,
            enableNav && opt.index === 0 ? styles['headCell--first'] : '',
            enableNav && arrLastItem === opt.index ? styles['headCell--last'] : '',
            useRowCheckbox && opt.index === 0 ? styles.checkboxCol : ''
          )

          const defaultCellRender = () => {
            return (
              <Th<P, R>
                additionalStyles={opt.headerOptions?.style}
                className={headerCellClasses}
                key={`column-title-${opt.index}`}
                column={renderedColumn[opt.index]}
                columns={columns}
                headerLine={headerLine}
                headerLinesCount={columnsToRender.length}
                source={source}
                titleRender={titleRender}
                onPriorityChange={(diff: number) => {
                  const currIndex = columns.findIndex(
                    (col) => col.field === renderedColumn[opt.index].field
                  )
                  if (currIndex > -1) {
                    columns[currIndex].priority = Math.max(
                      (columns[currIndex].priority || 0) + diff,
                      0
                    )
                    const conflictIndex = columns.findIndex(
                      (el, i: number) =>
                        i !== currIndex && el.priority === columns[currIndex].priority
                    )
                    if (conflictIndex > -1) {
                      columns[conflictIndex].priority = Math.max(
                        (columns[conflictIndex].priority || 0) - diff,
                        0
                      )
                    }
                    if (cacheKey) {
                      set(cacheKey, JSON.stringify(columns))
                    }
                    OnPriorityChange(columns)
                  }
                }}
                hasHierarchy={hasHierarchy}
              />
            )
          }

          if (headerCellTpl) {
            return (
              <Fragment key={`column-title-${opt.index}`}>
                {headerCellTpl({
                  column: renderedColumn[opt.index],
                  additionalStyles: opt.headerOptions?.style,
                  className: headerCellClasses,
                  defaultRender: defaultCellRender,
                })}
              </Fragment>
            )
          }

          return defaultCellRender()
        }
        if (headerIterator) {
          return headerIterator({
            iterator: (options) => drawHeaderColumn(options),
            level: headerLine,
          })
        }

        return renderedColumn.map((item, index) => {
          return drawHeaderColumn({ index, headerOptions: { className: item.className } })
        })
      }

      const defaultRowTpl = ({
        headerLine,
        children,
        classes,
      }: {
        headerLine: number
        children: ReactNode
        classes: string
      }) => {
        return (
          <tr key={`header-line-${headerLine}`} className={classes}>
            {children}
          </tr>
        )
      }

      const HeaderRow = headerRowTpl || defaultRowTpl

      return (
        <HeaderRow
          key={headerLine}
          headerLine={headerLine}
          classes={classNames(classes?.header || '', styles.headRow)}
        >
          {hasHierarchy && headerLine === 0 ? (
            <th className={styles.hierarchyCell} rowSpan={columnsToRender.length} />
          ) : null}
          {renderColumns()}
          {rowActions && headerLine === 0 ? (
            <th className={styles.actionCell} rowSpan={columnsToRender.length} />
          ) : null}
        </HeaderRow>
      )
    },
    [
      headerRowTpl,
      headerCellTpl,
      OnPriorityChange,
      cacheKey,
      classes?.header,
      headerIterator,
      columns,
      columnsToRender.length,
      enableNav,
      hasHierarchy,
      useRowCheckbox,
      rowActions,
      source,
      titleRender,
    ]
  )

  useEffect(() => {
    if (cacheKey) {
      const result = get(cacheKey)
      if (result) {
        const data = JSON.parse(result)
        if (data && data.length > 0) {
          columns.forEach((column) => {
            const cacheColumn = data.find((el: any) => el.field === column.field)
            if (cacheColumn) {
              column.priority = cacheColumn.priority
            }
          })
          OnPriorityChange(columns)
        }
      }
    }
  }, [])

  return (
    <thead className={isHeaderSticky ? styles.stickyHeader : ''}>
      {columnsToRender.map((line, index) => {
        return renderLine([...line], index)
      })}
    </thead>
  )
}
